/**
 * Blok PQC-sleutel in Instellingen > Meer. Glasronde 0.9.178, Frank 19 oktober 2026.
 *
 * Back-up maken (L3b) vraagt een PQC-sleutel met een werkende export, en er was in de
 * app geen scherm om die te maken. Dit blok gebruikt de routes van H9 en zet de stappen
 * in hun volgorde: sleutel maken, exporteren, dan PQC aan of uit. Alleen een eigenaar
 * zet iets; de backend zegt `mag_zetten` en weigert een lid hoe dan ook.
 *
 * DE SLEUTEL KOMT NOOIT OP HET SCHERM: de export is een bestand op deze Mac, en het
 * blok toont alleen het pad en de vingerafdruk.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";

/** Welke stap het blok toont. PUUR. */
export function pqcStap(stand) {
  if (!stand) return "laden";
  if (!stand.sleutelbos) return "geen-sleutelbos";
  if (!stand.sleutel) return "maken";
  if (!stand.export) return "exporteren";
  return "klaar";
}

export default function PqcBlok() {
  const { t } = useTaal();
  const [stand, setStand] = useState(null);
  const [pad, setPad] = useState("");
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState("");

  const laden = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/pqc`);
      setStand(r?.data || null);
    } catch {
      setStand(null);
    }
  }, []);

  useEffect(() => { laden(); }, [laden]);

  const doe = async (pad_, na) => {
    setBezig(true);
    setFout("");
    try {
      const r = await axios.post(`${API}/pqc/${pad_}`);
      if (na) na(r?.data || {});
      await laden();
    } catch (e) {
      setFout(e?.response?.status === 403
        ? t("Alleen een eigenaar kan de PQC-sleutel beheren.")
        : t("Dat is niet gelukt. Probeer het opnieuw, of kijk of dit de app op de Mac is."));
    } finally {
      setBezig(false);
    }
  };

  const stap = pqcStap(stand);
  if (stap === "laden") return null;
  const mag = stand.mag_zetten === true;
  return (
    <div className="border rounded-lg p-4 space-y-3" id="pqc-blok" data-testid="pqc-blok" data-stap={stap}>
      <h4 className="font-medium text-sm">{t("PQC-sleutel")}</h4>
      <p className="text-xs text-muted-foreground">
        {t("Je eigen sleutel voor versleutelde back-ups en versleutelde opslag. Hij staat in de sleutelbos van deze Mac; de export is een bestand dat je buiten deze Mac bewaart.")}
      </p>
      {stap === "geen-sleutelbos" && (
        <p className="text-xs" data-testid="pqc-geen-sleutelbos">{t("Een PQC-sleutel kan alleen in de app op de Mac, want hij staat in de sleutelbos van de Mac.")}</p>
      )}
      {stap !== "geen-sleutelbos" && !mag && (
        <p className="text-xs text-muted-foreground" data-testid="pqc-geen-eigenaar">{t("Alleen een eigenaar kan de PQC-sleutel beheren.")}</p>
      )}
      {stap === "maken" && mag && (
        <div className="space-y-1">
          <p className="text-xs" data-testid="pqc-uitleg">{t("Stap 1 van 2: er is nog geen sleutel. Maak er een; een bestaande sleutel wordt nooit vervangen.")}</p>
          <Button type="button" variant="outline" size="sm" className="h-11 md:h-8" disabled={bezig}
                  onClick={() => doe("sleutel")} data-testid="pqc-maak">{t("Sleutel maken")}</Button>
        </div>
      )}
      {(stap === "exporteren" || stap === "klaar") && (
        <p className="text-xs font-mono break-all" data-testid="pqc-vingerafdruk">{t("Vingerafdruk")}: {stand.vingerafdruk}</p>
      )}
      {stap === "exporteren" && mag && (
        <div className="space-y-1">
          <p className="text-xs" data-testid="pqc-uitleg">{t("Stap 2 van 2: exporteer de sleutel. Zonder export is een back-up niet terug te zetten als deze Mac weg is.")}</p>
          <Button type="button" variant="outline" size="sm" className="h-11 md:h-8" disabled={bezig}
                  onClick={() => doe("export", (d) => setPad(d.pad || ""))} data-testid="pqc-exporteer">{t("Sleutel exporteren")}</Button>
        </div>
      )}
      {pad && (
        <p className="text-xs" data-testid="pqc-pad">
          {t("Export geschreven naar")} <span className="font-mono break-all">{pad}</span>. {t("Bewaar dit bestand op een veilige plek buiten deze Mac: wie het kwijt is, is alles kwijt wat ermee versleuteld is.")}
        </p>
      )}
      {stap === "klaar" && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs" data-testid="pqc-stand">
            {stand.aan ? t("PQC-versleuteling staat aan.") : t("PQC-versleuteling staat uit.")} {t("De sleutel is geëxporteerd; een back-up kan nu.")}
          </p>
          <Switch checked={stand.aan === true} disabled={!mag || bezig}
                  onCheckedChange={(aan) => doe(aan ? "aan" : "uit")}
                  aria-label={t("PQC-versleuteling")} data-testid="pqc-switch"
                  className="relative flex-shrink-0 after:absolute after:-inset-3 after:content-['']" />
        </div>
      )}
      {fout && <p className="text-sm text-destructive" role="alert">{fout}</p>}
    </div>
  );
}
